import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, PieChart, Pie, Cell,
} from 'recharts';
import { Download, Save, RefreshCw, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import PanelConsumoIA from './PanelConsumoIA';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUSD = (v) => `$${Number(v || 0).toFixed(4)}`;
const fmtEUR = (v) => `€${Number(v || 0).toFixed(4)}`;
const COLORES = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444', '#84cc16'];

export default function ConfiguracionConsumoIA() {
  const [config, setConfig] = useState(null);
  const [saldo, setSaldo] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [evolucion, setEvolucion] = useState([]);
  const [detalle, setDetalle] = useState({ items: [], total: 0, page: 1 });
  const [diasEvo, setDiasEvo] = useState(30);
  const [filtroModulo, setFiltroModulo] = useState('todos');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form
  const [maxCredits, setMaxCredits] = useState('50');
  const [usdEurRate, setUsdEurRate] = useState('0.92');
  const [warnTh, setWarnTh] = useState('2');
  const [critTh, setCritTh] = useState('0.5');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, s, r, e, d] = await Promise.all([
        axios.get(`${API}/ia-consumo/config`),
        axios.get(`${API}/ia-consumo/saldo`),
        axios.get(`${API}/ia-consumo/resumen`),
        axios.get(`${API}/ia-consumo/evolucion?dias=${diasEvo}`),
        axios.get(`${API}/ia-consumo/detalle?page=1&page_size=100${filtroModulo !== 'todos' ? `&modulo=${filtroModulo}` : ''}`),
      ]);
      setConfig(cfg.data);
      setSaldo(s.data);
      setResumen(r.data);
      setEvolucion(e.data.datos || []);
      setDetalle(d.data);

      setMaxCredits(String(cfg.data.max_budget_credits));
      setUsdEurRate(String(cfg.data.usd_eur_rate));
      setWarnTh(String(cfg.data.threshold_warn_usd));
      setCritTh(String(cfg.data.threshold_critical_usd));
    } catch (err) {
      toast.error('Error cargando datos de consumo IA');
    } finally {
      setLoading(false);
    }
  }, [diasEvo, filtroModulo]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleGuardarConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ia-consumo/config`, {
        max_budget_credits: parseFloat(maxCredits),
        usd_eur_rate: parseFloat(usdEurRate),
        threshold_warn_usd: parseFloat(warnTh),
        threshold_critical_usd: parseFloat(critTh),
      });
      toast.success('Configuración guardada');
      await cargar();
    } catch (e) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const url = `${API}/ia-consumo/export-csv${filtroModulo !== 'todos' ? `?modulo=${filtroModulo}` : ''}`;
      const token = localStorage.getItem('lexduo_token');
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `consumo_ia_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success('CSV descargado');
    } catch (e) {
      toast.error('Error al exportar');
    }
  };

  if (loading) return <div className="p-6 text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="configuracion-consumo-ia">
      <PanelConsumoIA onResetSaldo={cargar} />

      <Tabs defaultValue="stats" className="w-full">
        <TabsList className="w-full justify-start">
          <TabsTrigger value="stats" data-testid="tab-stats">Estadísticas</TabsTrigger>
          <TabsTrigger value="detalle" data-testid="tab-detalle">Detalle</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config">Configuración</TabsTrigger>
        </TabsList>

        {/* === ESTADÍSTICAS === */}
        <TabsContent value="stats" className="space-y-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Evolución últimos:</Label>
            <Select value={String(diasEvo)} onValueChange={(v) => setDiasEvo(parseInt(v))}>
              <SelectTrigger className="w-32" data-testid="select-dias-evolucion">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 días</SelectItem>
                <SelectItem value="30">30 días</SelectItem>
                <SelectItem value="90">90 días</SelectItem>
                <SelectItem value="365">1 año</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="ghost" onClick={cargar} className="ml-auto">
              <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
            </Button>
          </div>

          {/* Gráfico evolución */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Consumo diario</CardTitle>
            </CardHeader>
            <CardContent style={{ height: 280 }}>
              {evolucion.length === 0 ? (
                <div className="text-center text-slate-400 py-12">Sin datos en el rango seleccionado</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={evolucion}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(2)}`} />
                    <Tooltip
                      formatter={(v, name) => name === 'usd' ? fmtUSD(v) : name === 'eur' ? fmtEUR(v) : v}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="usd" stroke="#6366f1" strokeWidth={2} name="USD" />
                    <Line type="monotone" dataKey="llamadas" stroke="#ec4899" strokeWidth={1} name="Llamadas" yAxisId={0} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Por módulo */}
            <Card>
              <CardHeader><CardTitle className="text-base">Por módulo</CardTitle></CardHeader>
              <CardContent style={{ height: 240 }}>
                {(resumen?.por_modulo || []).length === 0 ? (
                  <div className="text-center text-slate-400 py-12">Sin datos</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={resumen.por_modulo}
                        dataKey="usd"
                        nameKey="modulo"
                        outerRadius={80}
                        label={(e) => `${e.modulo}: ${fmtUSD(e.usd)}`}
                      >
                        {resumen.por_modulo.map((_, i) => (
                          <Cell key={i} fill={COLORES[i % COLORES.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Por modelo */}
            <Card>
              <CardHeader><CardTitle className="text-base">Por modelo (top 10)</CardTitle></CardHeader>
              <CardContent style={{ height: 240 }}>
                {(resumen?.por_modelo || []).length === 0 ? (
                  <div className="text-center text-slate-400 py-12">Sin datos</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={resumen.por_modelo} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(v) => `$${v.toFixed(2)}`} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="modelo" tick={{ fontSize: 10 }} width={140} />
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                      <Bar dataKey="usd" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* === DETALLE === */}
        <TabsContent value="detalle" className="space-y-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Módulo:</Label>
            <Select value={filtroModulo} onValueChange={setFiltroModulo}>
              <SelectTrigger className="w-40" data-testid="select-filtro-modulo">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="emails">Emails</SelectItem>
                <SelectItem value="tasas">Tasas</SelectItem>
                <SelectItem value="clientes">Clientes</SelectItem>
                <SelectItem value="chat_legal">Chat Legal</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleExportCSV} variant="outline" size="sm" className="ml-auto" data-testid="btn-export-csv">
              <Download className="w-4 h-4 mr-1" /> Exportar CSV
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[500px]">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Fecha</th>
                      <th className="text-left p-2">Módulo</th>
                      <th className="text-left p-2">Modelo</th>
                      <th className="text-right p-2">Tokens</th>
                      <th className="text-right p-2">USD</th>
                      <th className="text-right p-2">EUR</th>
                      <th className="text-left p-2">Estado</th>
                    </tr>
                  </thead>
                  <tbody data-testid="tabla-detalle-consumo">
                    {detalle.items.length === 0 && (
                      <tr><td colSpan={7} className="text-center p-4 text-slate-400">Sin registros</td></tr>
                    )}
                    {detalle.items.map((it) => (
                      <tr key={it.id} className="border-t hover:bg-slate-50">
                        <td className="p-2 text-xs">{new Date(it.timestamp).toLocaleString('es-ES')}</td>
                        <td className="p-2"><Badge variant="outline">{it.modulo}</Badge></td>
                        <td className="p-2 text-xs font-mono">{it.modelo}</td>
                        <td className="p-2 text-right text-xs text-slate-500">
                          {it.input_tokens} / {it.output_tokens}
                        </td>
                        <td className="p-2 text-right font-mono">{fmtUSD(it.cost_usd)}</td>
                        <td className="p-2 text-right font-mono text-slate-500">{fmtEUR(it.cost_eur)}</td>
                        <td className="p-2">
                          <Badge variant={it.status === 'ok' ? 'default' : 'destructive'}>
                            {it.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-2 text-xs text-slate-500 border-t">
                Mostrando {detalle.items.length} de {detalle.total} registros
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* === CONFIGURACIÓN === */}
        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="w-5 h-5" /> Configuración
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cfg-max-credits">Créditos transferidos al Universal LLM Key</Label>
                  <Input
                    id="cfg-max-credits" type="number" step="0.01" min="0"
                    value={maxCredits} onChange={(e) => setMaxCredits(e.target.value)}
                    data-testid="input-max-credits"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Créditos Emergent que has transferido al LLM Key.
                    Equivale a <strong>${(parseFloat(maxCredits || 0) * (config?.credit_rate_usd || 0.20)).toFixed(2)} USD</strong>
                    {' '}(1 crédito = ${(config?.credit_rate_usd || 0.20).toFixed(2)}).
                    Revísalo en Emergent → Profile → Universal Key.
                  </div>
                </div>
                <div>
                  <Label htmlFor="cfg-usd-eur">Ratio USD → EUR</Label>
                  <Input
                    id="cfg-usd-eur" type="number" step="0.0001" min="0"
                    value={usdEurRate} onChange={(e) => setUsdEurRate(e.target.value)}
                    data-testid="input-usd-eur"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Auto-actualizado cada 12h desde API externa. Fuente actual:{' '}
                    <Badge variant="outline" className="ml-1">{config?.usd_eur_source}</Badge>
                  </div>
                </div>
                <div>
                  <Label htmlFor="cfg-warn">Umbral alerta amarilla (USD)</Label>
                  <Input
                    id="cfg-warn" type="number" step="0.01" min="0"
                    value={warnTh} onChange={(e) => setWarnTh(e.target.value)}
                    data-testid="input-warn-threshold"
                  />
                </div>
                <div>
                  <Label htmlFor="cfg-crit">Umbral alerta roja (USD)</Label>
                  <Input
                    id="cfg-crit" type="number" step="0.01" min="0"
                    value={critTh} onChange={(e) => setCritTh(e.target.value)}
                    data-testid="input-crit-threshold"
                  />
                </div>
              </div>
              <Button onClick={handleGuardarConfig} disabled={saving} data-testid="btn-guardar-config">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando…' : 'Guardar configuración'}
              </Button>

              <div className="text-xs text-slate-500 pt-3 border-t">
                <strong>Importante:</strong> Los costes son estimaciones basadas en tarifas públicas de cada proveedor.
                Emergent aplica su propio margen que NO podemos consultar programáticamente. Para el saldo real acude a
                Emergent → Profile → Universal Key. Tras recargar saldo ahí, pulsa "He recargado" en el panel superior
                para sincronizar el contador.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
